import { useCallback, useState, useRef, useEffect } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import Autosuggest from "react-autosuggest";
import {
  mapStyles,
  stateCoordinates,
  mapConfig,
  mapStyle,
  mapboxToken,
  scrollZoomGuard,
} from "../../config";
import StatePopup from "../StatePopup";
import MapButtons from "../MapButtons";
import states from "../../data/states";
import { statesViews } from "../../constants";
import * as layers from "../../layers";
import { states as statesList } from "../../utils";
import { StatesViewToggle, ToggleButton, MapSearchAutosuggest } from "./styled.js";
import Legend from "../../components/Legend";
import { LegendSwatch } from "../../components/Legend/styled.js";
import { Wrapper, Container } from "../../components/App/styled.js";
import { MapFadeIn } from "../../components/MapChrome/styled.js";
import {
  Input,
  Form,
  Icon,
  CancelButton,
  CancelIcon,
} from "../../components/Search/styled.js";

function StatesMap({ className, ...props }) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  const [statesView, setStatesView] = useState(statesViews.CAPACITY);
  const [activeState, setActiveState] = useState("");
  const [activeHoverState, setActiveHoverState] = useState("");
  const [state, setState] = useState(null);
  const [mapViewState, setMapViewState] = useState(mapConfig);
  const mapRef = useRef();
  const resizeObserverRef = useRef();
  const styleLoadedRef = useRef(false);
  const [suggestions, setSuggestions] = useState([]);
  const [value, setValue] = useState("");
  const [interacted, setInteracted] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);

  useEffect(() => {
    setLegendOpen(false);
  }, [statesView]);

  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.resize();
      mapRef.current?.fitBounds(mapConfig.initialBounds, {
        padding: mapConfig.padding,
        duration: 0,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  // Safety net: reveal the map even if 'styledata' never fires (e.g. a
  // stalled style request), rather than leaving it hidden indefinitely.
  useEffect(() => {
    const timer = setTimeout(() => setMapReady(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const revealMap = useCallback(() => {
    mapRef.current?.resize();
    resizeObserverRef.current = new ResizeObserver(() => mapRef.current?.resize());
    resizeObserverRef.current.observe(mapRef.current.getContainer());
    mapRef.current.fitBounds(mapConfig.initialBounds, {
      padding: mapConfig.padding,
      duration: 0,
    });
    setMapReady(true);
  }, []);

  // Reveal as soon as the style (layers/camera) is ready, rather than
  // waiting for 'load', which also waits on the viewport's tiles to finish
  // downloading. Tiles then populate progressively underneath, same as
  // before the fade-in was added.
  const onStyleData = useCallback(
    (event) => {
      if (event.dataType !== "style" || styleLoadedRef.current) return;
      styleLoadedRef.current = true;
      revealMap();
    },
    [revealMap]
  );

  const onClick = useCallback((event) => {
    setInteracted(true);
    const feature = event.features && event.features[0];

    if (
      feature &&
      (feature.layer.id === "state-fills-capacity" ||
        feature.layer.id === "state-fills-number-solar-schools" ||
        "states-hover")
    ) {
      const coordinates = stateCoordinates.find((x) => {
        return x[0] === feature.properties.name;
      });
      feature.properties.longitude = coordinates[7];
      feature.properties.latitude = coordinates[6];
      setState(feature.properties);
      setActiveState(feature.properties.name);
      mapRef.current.flyTo({
        center: [feature.properties.longitude, feature.properties.latitude],
        duration: 1000,
        offset: [0, 100],
      });
      setValue("");
    }
  }, []);

  const onHover = useCallback((event) => {
    const feature = event.features && event.features[0];
    if (
      feature?.layer.id === "state-fills-capacity" ||
      feature?.layer.id === "state-fills-number-solar-schools"
    ) {
      setActiveHoverState(feature.properties.name);
      mapRef.current.getCanvas().style.cursor = "pointer";
    } else {
      mapRef.current.getCanvas().style.cursor = "";
    }
    if (!feature) {
      setActiveHoverState("");
    }
  }, []);

  const onMouseLeave = useCallback((event) => {
    setActiveHoverState("");
  }, []);

  const clearState = () => {
    setState(null);
    setValue("");
  };

  // Teach Autosuggest how to calculate suggestions for any given input value.
  const getSuggestions = (value) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    return inputLength === 0
      ? []
      : statesList.filter(
          (x) => x[0].toLowerCase().slice(0, inputLength) === inputValue
        );
  };

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  const getSuggestionValue = (suggestion) => suggestion[0];

  // Use your imagination to render suggestions.
  const renderSuggestion = (suggestion) => <div>{suggestion[0]}</div>;

  const onChange = (event, { newValue }) => {
    setValue(newValue);
  };

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value));
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (event, suggestion) => {
    const feature = states.features.find((feature) => {
      return feature.properties.name === suggestion.suggestionValue;
    });
    if (feature) {
      setState(feature.properties);
      setActiveState(feature.properties.name);
      mapRef.current.flyTo({
        center: [feature.properties.longitude, feature.properties.latitude],
        duration: 1000,
        offset: [0, 100],
      });
    } else {
      setState(null);
      setActiveState("");
    }
  };

  const renderInputComponent = (inputProps) => (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <Input {...inputProps} />
      {value.length > 0 && (
        <CancelButton aria-label="clear search" onClick={() => clearState()}>
          <CancelIcon viewBox="0 0 987.66 987.66">
            <path
              className="b"
              d="M970.38,17.28c-23.04-23.04-60.4-23.04-83.44,0L493.83,410.39,100.72,17.28c-23.04-23.04-60.4-23.04-83.44,0-23.04,23.04-23.04,60.4,0,83.44L410.39,493.83,17.28,886.94c-23.04,23.04-23.04,60.4,0,83.44,11.52,11.52,26.62,17.28,41.72,17.28s30.2-5.76,41.72-17.28l393.11-393.11,393.11,393.11c11.52,11.52,26.62,17.28,41.72,17.28s30.2-5.76,41.72-17.28c23.04-23.04,23.04-60.4,0-83.44l-393.11-393.11L970.38,100.72c23.04-23.04,23.04-60.4,0-83.44Z"
            />
          </CancelIcon>
        </CancelButton>
      )}
      <Icon src="/search.svg" alt="" />
    </Form>
  );

  const inputProps = {
    placeholder: isMobile ? "Search here" : "Search by state name",
    value,
    onChange: onChange,
  };

  return (
    <Wrapper>
      <MapSearchAutosuggest>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          onSuggestionSelected={onSuggestionSelected}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          renderInputComponent={renderInputComponent}
        />
      </MapSearchAutosuggest>
      {/* <MapSearch onSearch={handleSearch} placeholder="State Name" /> */}
      <Container>
        <StatesViewToggle>
          <ToggleButton
            type="blue"
            size={isMobile ? "small" : undefined}
            isActive={statesView === statesViews.CAPACITY}
            onClick={() => setStatesView(statesViews.CAPACITY)}
          >
            Solar Capacity
          </ToggleButton>
          <ToggleButton
            type="blue"
            size={isMobile ? "small" : undefined}
            isActive={statesView === statesViews.PERCENT_SCHOOLS}
            onClick={() => setStatesView(statesViews.PERCENT_SCHOOLS)}
          >
            Number of Solar Schools
          </ToggleButton>
        </StatesViewToggle>
        {statesView === statesViews.CAPACITY && (
          <Legend
            title="Solar Capacity"
            isOpen={legendOpen}
            onClose={() => setLegendOpen(false)}
          >
            <li>
              <LegendSwatch
                color={mapStyles.legendTier7Color}
              ></LegendSwatch>
              50,000+ kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier6Color}
              ></LegendSwatch>
              30,000-49,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier5Color}
              ></LegendSwatch>
              15,000-29,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier4Color}
              ></LegendSwatch>
              10,000-14,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier3Color}
              ></LegendSwatch>
              5,000-9,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier2Color}
              ></LegendSwatch>
              1,000-4,999 kW
            </li>
            <li>
              <LegendSwatch
                color={mapStyles.legendTier1Color}
              ></LegendSwatch>{" "}
              {`0-999 kW`}
            </li>
          </Legend>
        )}
        {statesView === statesViews.PERCENT_SCHOOLS && (
          <Legend
            title={
              <>
                Number of
                <br />
                Solar Schools
              </>
            }
            isOpen={legendOpen}
            onClose={() => setLegendOpen(false)}
          >
            <li>
              <LegendSwatch color={mapStyles.legendTier7Color}></LegendSwatch>
              500+
            </li>
            <li>
              <LegendSwatch color={mapStyles.legendTier6Color}></LegendSwatch>
              250-499
            </li>
            <li>
              <LegendSwatch color={mapStyles.legendTier5Color}></LegendSwatch>
              150-249
            </li>
            <li>
              <LegendSwatch color={mapStyles.legendTier4Color}></LegendSwatch>
              100-149
            </li>
            <li>
              <LegendSwatch color={mapStyles.legendTier3Color}></LegendSwatch>
              50-99
            </li>
            <li>
              <LegendSwatch color={mapStyles.legendTier2Color}></LegendSwatch>
              25-49
            </li>
            <li>
              <LegendSwatch color={mapStyles.legendTier1Color}></LegendSwatch>{" "}
              0-24
            </li>
          </Legend>
        )}
        <MapFadeIn $ready={mapReady}>
        <Map
          {...mapViewState}
          id="statesMap"
          ref={mapRef}
          onStyleData={onStyleData}
          onMove={(evt) => setMapViewState(evt.viewState)}
          scrollZoom={scrollZoomGuard === "click-to-activate" ? interacted : true}
          cooperativeGestures={scrollZoomGuard === "cooperative"}
          width="100%"
          height="100%"
          mapStyle={mapStyle}
          mapboxAccessToken={mapboxToken}
          onClick={onClick}
          onMouseMove={onHover}
          onMouseLeave={onMouseLeave}
          interactiveLayerIds={[
            "state-fills-capacity",
            "state-fills-number-solar-schools",
            "state-hover",
          ]}
        >
          <NavigationControl showCompass={false} />
          <Source id="states" type="geojson" data={states}>
            <Layer
              id="state-fills-capacity"
              {...layers.stateFillsCapacity}
              layout={{
                visibility:
                  statesView === statesViews.CAPACITY ? "visible" : "none",
              }}
            />
            <Layer
              id="state-fills-number-solar-schools"
              {...layers.stateFillsNumberSolarSchools}
              layout={{
                visibility:
                  statesView === statesViews.PERCENT_SCHOOLS
                    ? "visible"
                    : "none",
              }}
            />
            {/* <Layer
              id="states-hover"
              type="fill"
              paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
              filter={["==", "name", activeState]}
            /> */}
            <Layer
              id="state-hover"
              {...layers.stateHoverLayer}
              filter={["==", "name", activeHoverState]}
            />
            <Layer
              id="active-state"
              {...layers.activeStateLayer}
              filter={["==", "name", activeState]}
            />
            <Layer id="state-borders" {...layers.stateBordersStatesLayer} />
          </Source>
          {state && (
            <StatePopup
              anchor="bottom"
              longitude={Number(state.longitude)}
              latitude={Number(state.latitude)}
              onClose={() => clearState()}
              closeOnClick={false}
              data={state}
              view={statesView}
            ></StatePopup>
          )}
        </Map>{" "}
        </MapFadeIn>
        <MapButtons
          mapRef={mapRef}
          showStates={true}
          showLegend={true}
          legendOpen={legendOpen}
          onToggleLegend={() => setLegendOpen((open) => !open)}
          padding={mapConfig.padding}
        />
      </Container>
    </Wrapper>
  );
}

export default StatesMap;
